import { EvaluationResponse } from '@module/evaluation/model/EvaluationResponse';
import { BulkSplitExpenseEvaluationsAction } from '@state/types/BulkSplitExpenseEvaluationsAction';
import { ExpenseEvaluationMergeForm } from '@state/types/ExpenseEvaluationMergeForm';
import { mainRestApi } from '@state/stores/restApi/rest-api';
import { evaluationApi } from '../api';

export const bulkOperationsApi = evaluationApi.injectEndpoints({
  endpoints: (build) => ({
    bulkApproveEvaluationExpenses: build.mutation<
      EvaluationResponse,
      {
        id: string;
        expenseIds: number[];
        accountingCodeId: string | null;
        comments: string | null;
      }
    >({
      query: (data) => ({
        url: `/bulk-approve-expenses`,
        method: 'POST',
        data
      }),
      invalidatesTags: (res, _err, { id }) =>
        res ? [{ type: 'EVALUATION', id }] : ['EVALUATION']
    }),

    bulkSplitEvaluationExpenses: build.mutation<
      EvaluationResponse,
      BulkSplitExpenseEvaluationsAction
    >({
      query: (data) => ({
        url: `/bulk-split-expenses`,
        method: 'POST',
        data
      }),
      invalidatesTags: (res, _err, { id }) =>
        res ? [{ type: 'EVALUATION', id }] : ['EVALUATION']
    }),

    bulkApproveSplitEvaluationExpenses: build.mutation<
      EvaluationResponse,
      { evaluationId: string | number }
    >({
      query: ({ evaluationId }) => ({
        url: `/${evaluationId}/bulk-approve-pending-split-expenses`,
        method: 'POST'
      }),
      invalidatesTags: (res, _err, { evaluationId }) =>
        res ? [{ type: 'EVALUATION', evaluationId }] : ['EVALUATION']
    }),

    swapExpenseEvaluation: build.mutation<
      EvaluationResponse,
      {
        evaluationId: string | number;
        sourceExpenseId: number;
        destinationExpenseId: number;
      }
    >({
      query: ({ evaluationId, ...body }) => ({
        url: `/${evaluationId}/swap-expense`,
        method: 'POST',
        data: body
      }),
      invalidatesTags: (res, _err, { evaluationId }) =>
        res ? [{ type: 'EVALUATION', id: evaluationId }] : ['EVALUATION']
    }),

    undoSwapExpenseEvaluation: build.mutation<
      EvaluationResponse,
      {
        evaluationId: string | number;
        expenseId: number;
      }
    >({
      query: ({ evaluationId, ...body }) => ({
        url: `/${evaluationId}/undo-swap-expense`,
        method: 'POST',
        data: body
      }),
      invalidatesTags: (res, _err, { evaluationId }) =>
        res ? [{ type: 'EVALUATION', id: evaluationId }] : ['EVALUATION']
    }),

    mergeExpenses: build.mutation<
      EvaluationResponse,
      {
        evaluationId: string | number;
        form: ExpenseEvaluationMergeForm;
      }
    >({
      query: ({ evaluationId, form }) => ({
        url: `/${evaluationId}/merge-expense`,
        method: 'POST',
        data: form
      }),
      invalidatesTags: (res, _err, { evaluationId }) =>
        res ? [{ type: 'EVALUATION', id: evaluationId }] : ['EVALUATION'],
      async onQueryStarted(_arg, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            mainRestApi.util.invalidateTags([
              { type: 'SECTION_PORT_EXPENSES', id: data.proforma.port.id }
            ])
          );
        } catch {
          // Only invalidate cross-API tags on success
        }
      }
    }),

    undoMergeExpense: build.mutation<
      EvaluationResponse,
      {
        evaluationId: string | number;
        expenseEvaluationId: number;
      }
    >({
      query: ({ evaluationId, expenseEvaluationId }) => ({
        url: `/${evaluationId}/expenseEvaluation/${expenseEvaluationId}/undo-merge-expense`,
        method: 'POST'
      }),
      invalidatesTags: (res, _err, { evaluationId }) =>
        res ? [{ type: 'EVALUATION', id: evaluationId }] : ['EVALUATION'],
      async onQueryStarted(_arg, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            mainRestApi.util.invalidateTags([
              { type: 'SECTION_PORT_EXPENSES', id: data.proforma.port.id }
            ])
          );
        } catch {
          // Only invalidate cross-API tags on success
        }
      }
    })
  }),
  overrideExisting: false
});

export const {
  useBulkApproveEvaluationExpensesMutation,
  useBulkSplitEvaluationExpensesMutation,
  useBulkApproveSplitEvaluationExpensesMutation,
  useSwapExpenseEvaluationMutation,
  useUndoSwapExpenseEvaluationMutation,
  useMergeExpensesMutation,
  useUndoMergeExpenseMutation
} = bulkOperationsApi;
